/** Linked devices domain — SPEC §5.4 / FR-DEV-* */

import type { DeviceLinkOffer, DeviceRecord, DeviceTrustState } from "@veto/protocol";
import { VetoError } from "./errors";

export const DEVICE_LINK_TTL_MS = 5 * 60 * 1000;

export function createLocalDevice(input: {
  name: string;
  platform: DeviceRecord["platform"];
  fingerprintHex: string;
}): DeviceRecord {
  return {
    deviceId: randomHex(16),
    name: input.name.trim() || "This device",
    platform: input.platform,
    fingerprint: input.fingerprintHex,
    trustState: "trusted",
    lastSeenAt: Date.now(),
  };
}

export function createLinkOffer(input: {
  newDeviceFingerprint: string;
  platform: DeviceRecord["platform"];
  name: string;
  now?: number;
}): DeviceLinkOffer {
  const now = input.now ?? Date.now();
  return {
    v: 1,
    type: "device.link.offer",
    challenge: randomHex(24),
    newDeviceFingerprint: input.newDeviceFingerprint,
    platform: input.platform,
    name: input.name.trim() || "New device",
    expiresAt: now + DEVICE_LINK_TTL_MS,
  };
}

export function encodeLinkOffer(offer: DeviceLinkOffer): string {
  return `veto-link:${btoa(JSON.stringify(offer))}`;
}

export function decodeLinkOffer(payload: string): DeviceLinkOffer {
  const raw = payload.startsWith("veto-link:") ? payload.slice("veto-link:".length) : payload;
  let offer: DeviceLinkOffer;
  try {
    offer = JSON.parse(atob(raw)) as DeviceLinkOffer;
  } catch {
    throw new VetoError("VALIDATION_FAILED", "Invalid device link QR payload");
  }
  if (offer?.type !== "device.link.offer" || !offer.challenge || !offer.expiresAt) {
    throw new VetoError("VALIDATION_FAILED", "Malformed device link offer");
  }
  return offer;
}

const DEVICE_APPROVAL_DOMAIN = "veto/v1/device-approve|";

/**
 * Canonical approval bytes (FR-DEV-001): challenge, new-device fingerprint and
 * device id, approver device id, approval time. Signed with the account
 * messaging Ed25519 key inside WASM — raw keys never cross into UI code.
 */
export function linkApprovalBytes(input: {
  challenge: string;
  newDeviceFingerprint: string;
  newDeviceId: string;
  approvedByDeviceId: string;
  approvedAt: number;
}): Uint8Array {
  const parts = [
    input.challenge,
    input.newDeviceFingerprint.toLowerCase(),
    input.newDeviceId,
    input.approvedByDeviceId,
    String(input.approvedAt),
  ];
  return new TextEncoder().encode(`${DEVICE_APPROVAL_DOMAIN}${parts.join("|")}`);
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function approveLinkOffer(
  devices: DeviceRecord[],
  offer: DeviceLinkOffer,
  approvedByDeviceId: string,
  now = Date.now(),
  /** Ed25519 signer for the account messaging key (WASM). Required — approvals are never unsigned. */
  sign?: (message: Uint8Array) => Uint8Array,
): { devices: DeviceRecord[]; certificate: string } {
  if (now > offer.expiresAt) {
    throw new VetoError("VALIDATION_FAILED", "Device link offer expired");
  }
  if (!devices.some((d) => d.deviceId === approvedByDeviceId && d.trustState === "trusted")) {
    throw new VetoError("PERMISSION_DENIED", "Approver device is not trusted");
  }
  if (devices.some((d) => d.fingerprint === offer.newDeviceFingerprint && d.trustState !== "revoked")) {
    throw new VetoError("VALIDATION_FAILED", "Device already linked");
  }
  if (!sign) {
    throw new VetoError("VALIDATION_FAILED", "Account signer unavailable — unlock and retry");
  }
  const newDeviceId = randomHex(16);
  const sig = sign(
    linkApprovalBytes({
      challenge: offer.challenge,
      newDeviceFingerprint: offer.newDeviceFingerprint,
      newDeviceId,
      approvedByDeviceId,
      approvedAt: now,
    }),
  );
  if (sig.length !== 64) {
    throw new VetoError("VALIDATION_FAILED", "Approval signature malformed");
  }
  const approvalSigHex = bytesToHex(sig);
  const certificate = `cert:${offer.challenge}:${approvedByDeviceId}:${approvalSigHex}`;
  const linked: DeviceRecord = {
    deviceId: newDeviceId,
    name: offer.name,
    platform: offer.platform,
    fingerprint: offer.newDeviceFingerprint,
    trustState: "trusted",
    lastSeenAt: now,
    approvedByDeviceId,
    approvedAt: now,
    approvalChallenge: offer.challenge,
    approvalSigHex,
  };
  return { devices: [...devices, linked], certificate };
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.replace(/\s+/g, "").toLowerCase();
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
    if (!Number.isInteger(byte)) throw new VetoError("VALIDATION_FAILED", "Malformed key encoding");
    out[i] = byte;
  }
  return out;
}

/**
 * Verify a stored approval certificate against the account messaging key.
 * Records without certificates (implicit self, pre-certificate registries)
 * return null (unknown) instead of false — callers display the state and keep
 * revoke available; they never auto-nuke the registry on a verify failure.
 */
export function verifyLinkApproval(
  record: DeviceRecord,
  accountMessagingKeyHex: string,
  verify: (pk: Uint8Array, message: Uint8Array, sig: Uint8Array) => boolean,
): boolean | null {
  if (!record.approvalSigHex || !record.approvedByDeviceId || !record.approvedAt) return null;
  if (
    !record.approvalChallenge ||
    !/^[0-9a-fA-F]{128}$/.test(record.approvalSigHex) ||
    !/^[0-9a-fA-F]{64}$/.test(accountMessagingKeyHex)
  ) {
    return false;
  }
  try {
    return verify(
      hexToBytes(accountMessagingKeyHex),
      linkApprovalBytes({
        challenge: record.approvalChallenge,
        newDeviceFingerprint: record.fingerprint,
        newDeviceId: record.deviceId,
        approvedByDeviceId: record.approvedByDeviceId,
        approvedAt: record.approvedAt,
      }),
      hexToBytes(record.approvalSigHex),
    );
  } catch {
    return false;
  }
}

/** FR-DEV-003: revoke marks device and signals session rotation required. */
export function revokeDevice(
  devices: DeviceRecord[],
  deviceId: string,
  now = Date.now(),
): { devices: DeviceRecord[]; rotateSessions: true } {
  const next = devices.map((d) =>
    d.deviceId === deviceId
      ? { ...d, trustState: "revoked" as DeviceTrustState, revokedAt: now }
      : d,
  );
  if (!next.some((d) => d.deviceId === deviceId && d.trustState === "revoked")) {
    throw new VetoError("VALIDATION_FAILED", "Unknown device");
  }
  return { devices: next, rotateSessions: true };
}

function randomHex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return [...arr].map((b) => b.toString(16).padStart(2, "0")).join("");
}
